import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, Card, CardHeader, AtomBadge, StatusPill } from "../components/primitives";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const TABS = ["Overview","Performance","Validation history","Feature importance","Training data","Tuning","Change log","Documentation"];

export default function ModelDetail() {
  const { mid } = useParams();
  const [m, setM] = useState(null);
  const [tab, setTab] = useState("Overview");
  useEffect(() => { api.get(`/models/${mid}`).then(r => setM(r.data)); }, [mid]);
  if (!m) return <div className="p-6 text-[13px] text-ink-muted">Loading model…</div>;

  const perf = Array.from({length:26}, (_, i) => ({
    week: `W${i+1}`,
    precision: +(m.precision - 0.04 + (i * 0.002) + (Math.sin(i)*0.005)).toFixed(3),
    recall: +(m.recall - 0.05 + (i * 0.003) + (Math.cos(i)*0.005)).toFixed(3),
  }));

  return (
    <div>
      <PageHeader
        title={`${m.name} ${m.version}`}
        subtitle={m.purpose}
        sections={["AI/ML"]}
        atom={<AtomBadge model={m.name} version={m.version} />}
      />
      <div className="px-6 border-b border-line bg-bg-surface">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map(t => (
            <button key={t} data-testid={`model-tab-${t.replace(/\s+/g,'-').toLowerCase()}`} onClick={() => setTab(t)} className={`px-3 py-2 text-[12px] border-b-2 -mb-px whitespace-nowrap ${tab===t?'border-amber text-ink font-medium':'border-transparent text-ink-muted hover:text-ink'}`}>{t}</button>
          ))}
        </div>
      </div>
      <div className="p-6 space-y-3">
        {tab === "Overview" && (
          <div className="grid grid-cols-3 gap-3">
            <Card>
              <CardHeader title="Model metadata" />
              <div className="p-3 text-[12px] space-y-1.5">
                {[["Name", m.name],["Version", m.version],["Owner", "Atom Model Operations"],["Responsible", "CCO + Head of AML"],["Deployment", m.deployment],["Last validation", m.last_validation],["Next validation", m.next_validation],["Status", m.status]].map(([k,v]) => (
                  <div key={k} className="flex justify-between border-b border-line/50 py-1"><span className="text-ink-subtle">{k}</span><span className="font-medium">{v}</span></div>
                ))}
              </div>
            </Card>
            <Card className="col-span-2">
              <CardHeader title="Description" />
              <div className="p-3 text-[13px] leading-relaxed">{m.purpose}</div>
              <div className="px-3 pb-3 text-[11px] text-ink-subtle">
                Explainability score: <span className="font-medium text-ink">{m.explainability?.toFixed(2)}</span> (Atom Photon v1.0 assessment) — meets directive glass-box threshold of 0.85.
              </div>
            </Card>
          </div>
        )}
        {tab === "Performance" && (
          <Card>
            <CardHeader title="Performance over 26 weeks" subtitle="Precision · Recall · Drift" />
            <div className="p-3 h-[320px]">
              <ResponsiveContainer>
                <LineChart data={perf}>
                  <CartesianGrid strokeDasharray="2 2" stroke="#E5E5E0" vertical={false} />
                  <XAxis dataKey="week" tick={{fontSize:10,fill:'#5C5C57'}} axisLine={{stroke:'#E5E5E0'}} tickLine={false} />
                  <YAxis domain={[0.7, 1]} tick={{fontSize:10,fill:'#5C5C57'}} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{fontSize:11}} />
                  <Line type="monotone" dataKey="precision" stroke="#0B2B5C" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="recall" stroke="#F59A2D" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-4 border-t border-line">
              {[["Precision", m.precision],["Recall", m.recall],["Latency p50", `${m.latency_p50}ms`],["Latency p95", `${m.latency_p95}ms`]].map(([k,v]) => (
                <div key={k} className="p-3 border-r border-line last:border-0"><div className="text-[10px] text-ink-subtle uppercase">{k}</div><div className="text-[16px] font-medium tabular">{typeof v === 'number' ? v.toFixed(2) : v}</div></div>
              ))}
            </div>
          </Card>
        )}
        {tab === "Validation history" && (
          <Card><CardHeader title="Validation history" />
            <table className="dense w-full"><thead><tr><th>Date</th><th>Validator</th><th>Scope</th><th>Outcome</th><th>Report</th></tr></thead><tbody>
              {[
                ["14 Apr 2026","KPMG (independent)","Annual full validation","Pass — 3 minor findings","AE-VAL-2026-Q2.pdf"],
                ["12 Jan 2026","Atom team (internal)","Pre-deployment","Pass","AE-PRE-2026-01.pdf"],
                ["18 Oct 2025","Internal Audit","Quarterly","Pass","AE-IA-2025-Q4.pdf"],
              ].map((r,i) => <tr key={i}>{r.map((c,j) => <td key={j} className="text-[12px]">{j===4 ? <button className="text-amber-600">{c}</button> : c}</td>)}</tr>)}
            </tbody></table>
          </Card>
        )}
        {tab === "Feature importance" && (
          <Card><CardHeader title="Feature importance (top 12)" right={<AtomBadge model="Atom Photon" version="v1.0" />} />
            <div className="p-3 space-y-1.5">
              {[
                ["Customer 90-day velocity baseline", 0.18],["Counterparty risk score", 0.12],["Hour-of-day deviation", 0.10],
                ["Channel mix entropy", 0.09],["Amount above customer p95", 0.085],["Geographic spread of counterparties", 0.08],
                ["Beneficiary recency", 0.075],["Cross-border ratio", 0.07],["Round-number flag", 0.045],
                ["Cash/electronic ratio", 0.04],["Branch frequency", 0.035],["Time since last alert", 0.03],
              ].map(([f, v]) => (
                <div key={f}>
                  <div className="flex justify-between text-[12px]"><span>{f}</span><span className="tabular">{(v*100).toFixed(1)}%</span></div>
                  <div className="h-1.5 bg-bg-subtle"><div className="h-full bg-navy" style={{width: `${v*100*4}%`}} /></div>
                </div>
              ))}
            </div>
          </Card>
        )}
        {tab === "Training data" && (
          <Card><CardHeader title="Training data lineage" />
            <div className="p-3 text-[12px] space-y-2">
              <div className="flex justify-between border-b border-line/50 py-1"><span className="text-ink-subtle">Training period</span><span>01 Jan 2024 — 31 Dec 2025</span></div>
              <div className="flex justify-between border-b border-line/50 py-1"><span className="text-ink-subtle">Volume</span><span className="tabular">42.6M transactions · 1.8M customers</span></div>
              <div className="flex justify-between border-b border-line/50 py-1"><span className="text-ink-subtle">Source systems</span><span>Finacle CBS · CMS · NIBSS · IB/MB feeds · sanctioned-typology corpus</span></div>
              <div className="flex justify-between border-b border-line/50 py-1"><span className="text-ink-subtle">Data quality</span><span>0.98 completeness · 0.97 consistency</span></div>
              <div className="flex justify-between border-b border-line/50 py-1"><span className="text-ink-subtle">Last refresh</span><span>18 Feb 2026</span></div>
              <div className="flex justify-between border-b border-line/50 py-1"><span className="text-ink-subtle">Drift detection</span><span className="text-risk-medium">Counterparty-risk feature: +6.2% PSI · retraining flagged</span></div>
            </div>
          </Card>
        )}
        {tab === "Tuning" && (
          <Card><CardHeader title="Threshold tuning log" subtitle="Maker-checker enforced on every change" />
            <table className="dense w-full"><thead><tr><th>Date</th><th>Change</th><th>Maker</th><th>Checker</th><th>Impact</th></tr></thead><tbody>
              {[
                ["12 May 2026","Sub-CTR structuring threshold ₦4.95M → ₦4.9M","Daniel Castellanos","Marcus Whitfield","Recall +0.014, FP rate +0.4%"],
                ["28 Apr 2026","Velocity 24h threshold ×2.5 → ×3.0","Noah Kessler","Marcus Whitfield","FP rate -1.1%"],
                ["18 Mar 2026","Cross-border high-velocity rule expanded","Daniel Castellanos","Marcus Whitfield","Recall +0.023"],
              ].map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className="text-[12px]">{c}</td>)}</tr>)}
            </tbody></table>
          </Card>
        )}
        {tab === "Change log" && (
          <Card><CardHeader title="Governance change log" />
            <div className="p-3 text-[12px] space-y-2">
              {[
                ["22 Apr 2026","Annual validation by KPMG completed; 3 minor findings logged","CCO ratified"],
                ["18 Feb 2026","Retraining cycle completed on 14-month corpus","Head of AML approved"],
                ["12 Jan 2026","v1.1 deployed to production after parallel-run","Board ratified"],
              ].map((r, i) => <div key={i} className="border-b border-line/50 pb-2"><div className="text-[11px] text-ink-subtle">{r[0]}</div><div>{r[1]}</div><div className="text-[10px] text-ink-subtle italic">{r[2]}</div></div>)}
            </div>
          </Card>
        )}
        {tab === "Documentation" && (
          <Card><CardHeader title="Documentation" />
            <div className="p-3 grid grid-cols-2 gap-2 text-[12px]">
              {["Model card","Validation methodology","Independent validation report","Training data documentation","Approval records","Risk assessment"].map(d => (
                <div key={d} className="border border-line rounded-sm p-3 flex justify-between items-center"><span>{d}</span><button className="text-amber-600 text-[11px]">Download PDF</button></div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
